import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  Box, Button, Typography, IconButton, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Grid, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Skeleton, InputAdornment,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip, Stack
} from '@mui/material';
import { Add, Edit, Delete, Search, Groups, Clear, Close, Business, Computer } from '@mui/icons-material';
import { createDepartment, updateDepartment, deleteDepartment } from '../api/services/userService/Department';
import NodeAssignmentDialog from './NodeAssignmentDialog';

export default function DepartmentManagement({ userPermissions, sharedData, onRefresh }) {
  const { keycloak } = useKeycloak();
  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNodeDialog, setOpenNodeDialog] = useState(false);
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({ name: '', clientId: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const canManage = useCallback(() => {
    if (!userPermissions) return false;
    return userPermissions.isSuperAdmin ||
      Object.values(userPermissions.clientRoles || {}).includes('admin') ||
      Object.values(userPermissions.departmentRoles || {}).includes('admin');
  }, [userPermissions]);

  const filterByPermissions = useCallback((list, type) => {
    if (!userPermissions || userPermissions.isSuperAdmin) return list;
    if (type === 'clients') return list.filter(c => userPermissions.clientIds?.includes(c.id));
    return list.filter(d =>
      userPermissions.departmentIds?.includes(d.id) ||
      userPermissions.clientIds?.includes(d.clientId)
    );
  }, [userPermissions]);

  const filteredDepartments = useMemo(() => {
    let result = filterByPermissions(sharedData?.departments || [], 'departments');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d => d.name?.toLowerCase().includes(term));
    }
    if (selectedClientId) {
      result = result.filter(d => d.clientId === selectedClientId);
    }
    return result;
  }, [sharedData?.departments, searchTerm, selectedClientId, filterByPermissions]);

  const paginatedDepartments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDepartments.slice(start, start + rowsPerPage);
  }, [filteredDepartments, page, rowsPerPage]);

  useEffect(() => {
    if (sharedData) {
      setClients(filterByPermissions(sharedData.clients || [], 'clients'));
      setDepartments(paginatedDepartments);
    }
  }, [sharedData, paginatedDepartments, filterByPermissions]);

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '—';
  };

  const openCreateDialog = () => {
    setIsEdit(false);
    setFormData({ name: '', clientId: '' });
    setOpenDialog(true);
  };

  const openEditDialog = (dept) => {
    setIsEdit(true);
    setCurrentDepartment(dept);
    setFormData({ name: dept.name, clientId: dept.clientId });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setNotification({ open: true, message: 'Name required', severity: 'error' });
      return;
    }
    if (!isEdit && !formData.clientId) {
      setNotification({ open: true, message: 'Select a client', severity: 'error' });
      return;
    }

    try {
      await keycloak.updateToken(30);

      const res = isEdit
        ? await updateDepartment({ id: currentDepartment.id, name: formData.name }, keycloak.token)
        : await createDepartment({ name: formData.name, clientId: formData.clientId }, keycloak.token);

      if (res.success) {
        setNotification({ open: true, message: `Department ${isEdit ? 'updated' : 'created'}`, severity: 'success' });
        if (onRefresh) await onRefresh();
        setOpenDialog(false);
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await keycloak.updateToken(30);
      const res = await deleteDepartment(currentDepartment.id, keycloak.token);
      if (res.success) {
        setNotification({ open: true, message: 'Deleted', severity: 'success' });
        if (onRefresh) await onRefresh();
      } else {
        setNotification({ open: true, message: res.error || 'Failed', severity: 'error' });
      }
      setOpenDeleteDialog(false);
    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedClientId('');
    setPage(0);
  };

  const hasFilters = searchTerm || selectedClientId;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Departments</Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredDepartments.length} department{filteredDepartments.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {canManage() && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} size="small">
            Add Department
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
            sx={{ width: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClientId}
              label="Client"
              onChange={e => { setSelectedClientId(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button size="small" onClick={clearFilters} startIcon={<Clear />}>Clear</Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Created</TableCell>
              {canManage() && <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={150} /></TableCell>
                  <TableCell><Skeleton width={120} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  {canManage() && <TableCell><Skeleton width={60} /></TableCell>}
                </TableRow>
              ))
            ) : departments.length > 0 ? (
              departments.map(dept => (
                <TableRow key={dept.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: 1,
                          bgcolor: 'secondary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <Groups sx={{ fontSize: 18, color: 'white' }} />
                      </Box>
                      <Typography variant="body2" fontWeight={500}>{dept.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<Business sx={{ fontSize: '14px !important' }} />}
                      label={getClientName(dept.clientId)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {dept.createdAt ? new Date(dept.createdAt).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  {canManage() && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentDepartment(dept);
                          setOpenNodeDialog(true);
                        }}
                        title="Manage Nodes"
                      >
                        <Computer fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDialog(dept)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => { setCurrentDepartment(dept); setOpenDeleteDialog(true); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManage() ? 4 : 3} sx={{ py: 8, textAlign: 'center' }}>
                  <Groups sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No departments found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredDepartments.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit' : 'New'} Department</Typography>
            <IconButton onClick={() => setOpenDialog(false)} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth size="small" label="Name" required
              value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
            />
            {!isEdit && (
              <FormControl fullWidth size="small" required>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.clientId}
                  label="Client"
                  onChange={e => setFormData(p => ({ ...p, clientId: e.target.value }))}
                >
                  {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>{isEdit ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs">
        <DialogTitle>Delete Department</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{currentDepartment?.name}</strong>? This affects all teams.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <NodeAssignmentDialog
        open={openNodeDialog}
        onClose={() => setOpenNodeDialog(false)}
        entityType="department"
        entityId={currentDepartment?.id}
        entityName={currentDepartment?.name}
        onSuccess={onRefresh}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification(p => ({ ...p, open: false }))} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
